import {useState, useEffect, useCallback} from "react";
import {collection, doc, getDocs, updateDoc, deleteDoc} from "firebase/firestore";
import {useFirebaseState} from "../firebase/firebaseContext";

/**
 * Custom hook to load, manage, update, and delete received course leads
 * for a specific certified instructor.
 *
 * Stored under `/instructors/{instructorId}/leads/{leadId}`.
 */
export const useInstructorLeads = ({instructorId}) => {
    const {db} = useFirebaseState();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLeads = useCallback(async () => {
        if (!db || !instructorId) {
            setLeads([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const leadsRef = collection(db, "instructors", instructorId, "leads");
            const snapshot = await getDocs(leadsRef);

            const items = [];
            snapshot.forEach((docSnap) => {
                items.push({
                    id: docSnap.id,
                    ...docSnap.data(),
                });
            });

            // Sort descending by submittedAt (newest first)
            items.sort((a, b) => {
                const dateStrA = a.submittedAt || a.createdAt;
                const dateStrB = b.submittedAt || b.createdAt;
                const dateA = dateStrA ? new Date(dateStrA).getTime() : 0;
                const dateB = dateStrB ? new Date(dateStrB).getTime() : 0;
                return dateB - dateA;
            });

            setLeads(items);
        } catch (err) {
            console.error("Failed to fetch instructor leads:", err);
            setError("Kunne ikke laste henvendelser.");
        } finally {
            setLoading(false);
        }
    }, [db, instructorId]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    const updateLeadStatus = useCallback(
        async (leadId, newStatus) => {
            if (!db || !instructorId || !leadId) return;

            // Optimistic local state update
            setLeads((prev) =>
                prev.map((lead) => (lead.id === leadId ? {...lead, status: newStatus} : lead))
            );

            try {
                const leadDocRef = doc(db, "instructors", instructorId, "leads", leadId);
                await updateDoc(leadDocRef, {
                    status: newStatus,
                    updatedAt: new Date().toISOString(),
                });
            } catch (err) {
                console.error("Failed to update lead status:", err);
                // Revert on failure
                fetchLeads();
                throw err;
            }
        },
        [db, instructorId, fetchLeads]
    );

    const deleteLead = useCallback(
        async (leadId) => {
            if (!db || !instructorId || !leadId) return;

            // Optimistic local removal
            setLeads((prev) => prev.filter((lead) => lead.id !== leadId));

            try {
                const leadDocRef = doc(db, "instructors", instructorId, "leads", leadId);
                await deleteDoc(leadDocRef);
            } catch (err) {
                console.error("Failed to delete lead:", err);
                fetchLeads();
                throw err;
            }
        },
        [db, instructorId, fetchLeads]
    );

    return {
        leads,
        loading,
        error,
        updateLeadStatus,
        deleteLead,
        refresh: fetchLeads,
    };
};
